// Copyright 2022, Imprivata, Inc.  All rights reserved.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useCreateEnrollmentMutation,
  useGetGrantStatusQuery,
  useGetInquiryQuery,
  useGetPatientQuery,
  useGetPhotoQuery,
  usePostGrantMutation,
} from '../../../api/hooks';
import { InquiryStatus } from '../../../api/hooks/getInquiryQuery';
import {
  type EnrollPageError,
  useEnrollPageErrorHandling,
} from '../hooks/useEnrollPageErrorHandling';

export const useEnrollPageState = () => {
  const [error, setError] = useState<EnrollPageError>();
  const [showPhoto, setShowPhoto] = useState(false);
  const [enrollmentSuccess, setEnrollmentSuccess] = useState(false);
  const [continueOnOriginalDevice, setContinueOnOriginalDevice] = useState(false);
  const [showPatientAlreadyEnrolledError, setShowPatientAlreadyEnrolledError] = useState(false);

  const navigate = useNavigate();

  const {
    patient,
    error: patientError,
    isLoading: isPatientLoading,
    enable: enablePatientQuery,
  } = useGetPatientQuery(false);

  const { photo, isLoading: isPhotoLoading } = useGetPhotoQuery(patient?.photoUrl);

  const {
    grantStatus,
    isLoading: isGrantStatusLoading,
    enable: enableGrantStatusQuery,
  } = useGetGrantStatusQuery(false);

  const {
    createEnrollment,
    apiError: createEnrollmentError,
    isPending: isEnrollmentPending,
  } = useCreateEnrollmentMutation({
    onSuccess: (_) => {
      enablePatientQuery();
      enableGrantStatusQuery();
      setShowPhoto(true);
    },
  });

  const { isLoading: isInquiryLoading, error: inquiryError } = useGetInquiryQuery({
    onSuccess: (inquiryId, status, isAssociatedWithSession) => {
      if (isAssociatedWithSession && status === InquiryStatus.SUCCESS) {
        createEnrollment(inquiryId);
      } else {
        setContinueOnOriginalDevice(true);
      }
    },
  });

  const { postGrant } = usePostGrantMutation({});

  // Use error handling hook
  useEnrollPageErrorHandling({
    createEnrollmentError,
    patientError,
    patient,
    isPatientLoading,
    inquiryError,
    error,
    grantStatus,
    isGrantStatusLoading,
    onSetEnrollmentSuccess: setEnrollmentSuccess,
    onSetError: setError,
    onEnablePatientQuery: enablePatientQuery,
    onEnableGrantStatusQuery: enableGrantStatusQuery,
    onSetShowPatientAlreadyEnrolledError: setShowPatientAlreadyEnrolledError,
    onSetShowPhoto: setShowPhoto,
  });

  return {
    state: {
      continueOnOriginalDevice,
      createEnrollmentError,
      enrollmentSuccess,
      error,
      grantStatus,
      inquiryError,
      isEnrollmentPending,
      isGrantStatusLoading,
      isInquiryLoading,
      isPatientLoading,
      isPhotoLoading,
      patient,
      patientError,
      photo,
      showPatientAlreadyEnrolledError,
      showPhoto,
    },
    actions: {
      createEnrollment,
      enableGrantStatusQuery,
      enablePatientQuery,
      navigate,
      postGrant,
      setContinueOnOriginalDevice,
      setEnrollmentSuccess,
      setError,
      setShowPatientAlreadyEnrolledError,
      setShowPhoto,
    },
  };
};
